import { cn } from '@/shared/lib/cn'

export interface DesvioItem {
  id: string
  label: string
  detalle?: string
  /** Días: negativo = antes del objetivo, positivo = después. `null` = sin objetivo. */
  dias: number | null
}

export interface DeviationBarsProps {
  items: readonly DesvioItem[]
  /** Texto de los dos polos, para la leyenda. */
  antes: string
  despues: string
  sinObjetivo: string
  resumenAccesible: string
  className?: string
}

/**
 * Desvío contra la fecha prometida: la única métrica del tablero con dos polos.
 *
 * Una escala divergente con el cero en el medio, y no dos barras de largo: lo
 * que hay que ver de un vistazo es **de qué lado** cayó cada entrega, y recién
 * después cuánto.
 *
 * El par de colores no es rojo/verde —el choque clásico para quien no distingue
 * esos dos— sino el rojo de "vencido", que ya significa eso en toda la app, y el
 * cian de las series. Además cada barra lleva su signo escrito: el lado nunca
 * se comunica sólo con el color.
 */
export function DeviationBars({
  items,
  antes,
  despues,
  sinObjetivo,
  resumenAccesible,
  className,
}: DeviationBarsProps) {
  const escala = Math.max(1, ...items.map((i) => Math.abs(i.dias ?? 0)))

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex items-center gap-3 text-2xs text-text-2">
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="inline-block h-2 w-2 rounded-[1px]"
            style={{ backgroundColor: 'hsl(var(--chart-serie))' }}
          />
          {antes}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="inline-block h-2 w-2 rounded-[1px]"
            style={{ backgroundColor: 'hsl(var(--overdue))' }}
          />
          {despues}
        </span>
      </div>

      <ul className="flex flex-col gap-1.5" aria-label={resumenAccesible}>
        {items.map((item) => {
          const dias = item.dias
          const tarde = dias !== null && dias > 0
          const ancho = dias === null ? 0 : (Math.abs(dias) / escala) * 50

          return (
            <li key={item.id} className="flex items-center gap-2">
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-xs text-text-1">{item.label}</span>
                {item.detalle ? (
                  <span className="truncate text-2xs text-text-3">{item.detalle}</span>
                ) : null}
              </div>

              <div className="relative h-4 w-1/2 shrink-0">
                {/* El cero, donde se apoyan las dos mitades. */}
                <div
                  aria-hidden="true"
                  className="absolute top-0 bottom-0 left-1/2 w-px"
                  style={{ backgroundColor: 'hsl(var(--hairline))' }}
                />
                {dias === null ? null : (
                  <div
                    className="absolute top-1 h-2.5 rounded-[2px]"
                    style={{
                      width: `${Math.max(1, ancho)}%`,
                      left: tarde ? '50%' : `${50 - Math.max(1, ancho)}%`,
                      backgroundColor: tarde ? 'hsl(var(--overdue))' : 'hsl(var(--chart-serie))',
                    }}
                  />
                )}
              </div>

              <span
                className={cn(
                  'num w-16 shrink-0 text-right text-2xs',
                  dias === null ? 'text-text-3' : tarde ? 'text-overdue' : 'text-text-1',
                )}
              >
                {dias === null ? sinObjetivo : `${dias > 0 ? '+' : ''}${dias} d`}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
